/** Public journey presentation: only data already present in the authorized share payload. */
type Row = Record<string, unknown>;
const obj = (value: unknown): Row => value && typeof value === "object" && !Array.isArray(value) ? value as Row : {};
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.map(obj) : [];
const str = (value: unknown): string => typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
const validDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value + "T12:00:00Z")) && new Date(value + "T12:00:00Z").toISOString().slice(0,10) === value;
export function shiftDay(date: string, delta: number): string {
  if (!validDay(date)) return "";
  return new Date(Date.parse(date + "T12:00:00Z") + delta * 86400000).toISOString().slice(0, 10);
}
export function todayInZone(zone?: string): string {
  const now = new Date();
  if (zone) {
    try {
      const parts = new Intl.DateTimeFormat("en-GB", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
      const fields = Object.fromEntries(parts.map(part => [part.type, part.value]));
      return `${fields.year}-${fields.month}-${fields.day}`;
    } catch { /* Invalid or absent destination zone: use the viewer's clock, never invent a zone. */ }
  }
  const localYear = now.getFullYear();
  return `${localYear}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
export type PublicDayItem = {
  id: string; date: string; title: string; time: string; place: string;
  kind: string; completed: boolean; sourceKey: string; imageUrl: string; detailSection: string; detailId: string;
};
export type PublicDay = { date: string; items: PublicDayItem[] };
export type PublicTimeline = {
  phase: "before" | "during" | "after" | "undated";
  today: PublicDay; yesterday: PublicDay; tomorrow: PublicDay; dayAfter: PublicDay;
  first: PublicDay; second: PublicDay; last: PublicDay; daysUntil: number | null;
};
const catalog: Array<[string, string]> = [
  ["itinerary", "itinerary"], ["experiences", "experiences"], ["events", "events"],
  ["pointsOfInterest", "pointsOfInterest"], ["restaurants", "restaurants"],
  ["places", "places"], ["hotels", "hotels"]
];
function itemDate(row: Row, kind: string): string {
  const dates = kind === "hotels"
    ? [row.checkInDate, row.checkIn, row.startDate, row.date]
    : [row.date, row.visitDate, row.reservationDate, row.startDate, row.arrivalDate, row.departureDate];
  const date = dates.map(str).find(validDay);
  return date || "";
}
function getPhoto(snapshot: Row, sourceKey: string, entryPhotos: unknown): string {
  // Only photos from an explicitly shared photo section, not private local media.
  if (!Array.isArray(snapshot.photos) || obj(snapshot.presentation).showPhotos === false) return "";
  const photo = rows(entryPhotos).find(row => /^https:\/\//i.test(str(row.url))) || rows(snapshot.photos).find(row => str(row.sourceId) === sourceKey);
  const url = str(photo?.url);
  // No data URLs or non-https URLs are emitted by the public overview.
  return /^https:\/\//i.test(url) ? url : "";
}
export function publicTimelineItems(snapshot: Row): PublicDayItem[] {
  const all: PublicDayItem[] = [];
  const unique = new Set<string>();
  // itineraryDays is already filtered by the public-share projector, including source permissions.
  // Read it first so authorized derived stops win over duplicate category rows.
  const publicDays: Row[] = rows(snapshot.itineraryDays).flatMap(day => rows(day.entries).map(entry => ({ ...entry, date: str(entry.date) || str(day.date) })));
  const sources: Array<[string, string, Row[]]> = [["itineraryDays", "itinerary", publicDays], ...catalog.map(([source, kind]): [string, string, Row[]] => [source, kind, rows(snapshot[source])])];
  for (const [source, kind, items] of sources) {
    for (const [index, row] of items.entries()) {
      if (/^(climate|weather)$/i.test(str(row.sourceKind || row.sourceType))) continue;
      const date = itemDate(row, kind);
      const title = str(row.title || row.name || row.placeName || row.venue || row.city);
      if (!date || !title) continue;
      const key = `${source}:${str(row.id) || index}`;
      // Deduplicate the same event repeated in itinerary and a category section.
      const normalizedTitle = title.toLocaleLowerCase().replace(/^(percorso a|esplorare|esplora|visita a|route to|explore|visit)\s+/i, "");
      const kindMap: Record<string,string> = {city:"places",hotel:"hotels",restaurant:"restaurants",poi:"pointsOfInterest",experience:"experiences",event:"events"};
      const matchedKind = ["places", "hotels", "restaurants", "pointsOfInterest"].find(section => rows(snapshot[section]).some(candidate => str(candidate.name || candidate.title).toLocaleLowerCase() === normalizedTitle));
      const derivedMatch = publicDays.find(candidate => str(candidate.title).toLocaleLowerCase() === normalizedTitle && kindMap[str(candidate.sourceKind)]);
      const effectiveKind = kindMap[str(row.sourceKind)] || (kind === "itinerary" ? matchedKind || kindMap[str(derivedMatch?.sourceKind)] || kind : kind);
      const identity = `${date}|${effectiveKind}|${normalizedTitle}|${str(row.startTime || row.time)}`;
      if (unique.has(identity)) continue;
      unique.add(identity);
      const status = str(row.status).toLowerCase();
      const completed = row.completed === true || row.isCompleted === true || status === "completed" || status === "done";
      const details = rows(snapshot[effectiveKind]).find(candidate => str(candidate.id) === str(row.sourceId || row.id)) || rows(snapshot[effectiveKind]).find(candidate => str(candidate.title || candidate.name).toLocaleLowerCase() === title.toLocaleLowerCase());
      all.push({ id: key, sourceKey: source, date, title, detailSection: details ? effectiveKind : "itinerary", detailId: details ? str(details.id) : date,
        time: str(row.startTime || row.time || row.departureTime),
        place: str(row.city || row.address), kind: effectiveKind, completed,
        imageUrl: getPhoto(snapshot, str(row.sourceId || row.id), row.photos) });
    }
  }
  return all.sort((a,b) => a.date.localeCompare(b.date) || (a.time && !b.time ? -1 : !a.time && b.time ? 1 : a.time.localeCompare(b.time)) || a.title.localeCompare(b.title));
}
export function buildPublicTimeline(snapshot: Row, start: string, end: string, now: string): PublicTimeline {
  const entries = publicTimelineItems(snapshot);
  const on = (date: string): PublicDay => ({ date, items: entries.filter(item => item.date === date) });
  const phase = !validDay(start) || !validDay(end) || end < start || !validDay(now) ? "undated" : now < start ? "before" : now > end ? "after" : "during";
  const daysUntil = validDay(start) && validDay(now) && now < start
    ? Math.round((Date.parse(start + "T12:00:00Z") - Date.parse(now + "T12:00:00Z")) / 86400000)
    : null;
  const inTrip = (date: string) => (!validDay(start) || date >= start) && (!validDay(end) || date <= end);
  const populated = [...new Set(entries.map(item => item.date).filter(inTrip))].sort();
  const first = populated[0] || start;
  const second = populated[1] || shiftDay(start, 1);
  const last = populated[populated.length - 1] || end;
  return {
    phase, today: on(now), yesterday: on(shiftDay(now,-1)), tomorrow: on(shiftDay(now,1)), dayAfter: on(shiftDay(now,2)),
    first: on(first), second: on(second === first ? "" : second), last: on(last), daysUntil
  };
}
export function publicDateLabel(date: string, language: string): string {
  if (!validDay(date)) return "";
  return new Intl.DateTimeFormat(language === "en" ? "en-GB" : language === "es" ? "es-ES" : "it-IT", {
    day: "numeric", month: "short", timeZone: "UTC"
  }).format(new Date(date + "T12:00:00Z"));
}

/** Group only equivalent visits in an already-authorized public snapshot. Never mutate trip data. */
const fold = (v: unknown) => str(v).normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().replace(/[’']/g, "'").replace(/[–—]/g, "-").replace(/\s+/g, " ").trim();
function place(row: Row): string { return fold(row.placeName || row.title || row.name).replace(/^(?:percorso a|visita (?:a|di)|esplorare|esplora|route to|visit(?: to)?|explore|visitar|explorar)\s+/i, ""); }
function kind(row: Row): string { return str(row.sourceKind || row.sourceType || (row.type === "activity" ? "manual" : "")); }
const eligible = (row: Row) => ["poi", "experience", "manual"].includes(kind(row)) && row.type !== "transfer" && !row.transportModeId;
function same(a: Row, b: Row): boolean {
  if (!eligible(a) || !eligible(b) || !place(a) || place(a) !== place(b)) return false;
  if (str(a.date || a.visitDate) !== str(b.date || b.visitDate) || fold(a.city) !== fold(b.city)) return false;
  if (str(a.startTime || a.time) !== str(b.startTime || b.time) || str(a.endTime) !== str(b.endTime)) return false;
  if (a.googlePlaceId && b.googlePlaceId) return a.googlePlaceId === b.googlePlaceId;
  if (fold(a.address) && fold(a.address) === fold(b.address)) return true;
  return [a.latitude,a.longitude,b.latitude,b.longitude].every(v=>typeof v === "number" && Number.isFinite(v)) && Math.abs(Number(a.latitude)-Number(b.latitude)) < 0.0001 && Math.abs(Number(a.longitude)-Number(b.longitude)) < 0.0001;
}
export function groupPublicStops(items: Row[]): Row[] {
  const result: Row[] = [];
  for (const row of items) {
    if (/^(climate|weather)$/i.test(kind(row))) continue;
    const group = result.find(item=>same(item,row));
    if (!group) { result.push({ ...row }); continue; }
    const related = Array.isArray(group.relatedEntries) ? group.relatedEntries as Row[] : [];
    if (kind(row) === "poi" && kind(group) !== "poi") { const prior = { ...group }; delete prior.relatedEntries; Object.assign(group,row,{relatedEntries:[prior,...related]}); }
    else group.relatedEntries = [...related,{...row}];
  }
  return result;
}
